/**
 * PricingRuleTargetingWidget — friendly, editable "who can use it & how it behaves"
 * section for the Pricing Rule detail page. Surfaces ERPNext capabilities that were
 * previously only in the raw Party/Advanced field sections:
 *   • Audience   — applicable_for + the matching target (customer / group / territory / campaign)
 *   • Stacking   — apply_multiple_pricing_rules (combine with other promos)
 *   • Priority   — which rule wins when several match
 *   • Advanced   — discount on another item, recursion (buy-N-get repeat), threshold %, margin
 * All staged via onFieldChange; the page's Save persists.
 */

import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { NUM_INPUT_CLASS } from "./styles";

const pick = (th: boolean, t: string, e: string) => (th ? t : e);

/** applicable_for → the field + doctype that holds the chosen target. */
const TARGETS: Record<string, { field: string; doctype: string; th: string; en: string }> = {
  Customer: { field: "customer", doctype: "Customer", th: "ลูกค้า", en: "Customer" },
  "Customer Group": { field: "customer_group", doctype: "Customer Group", th: "กลุ่มลูกค้า", en: "Customer group" },
  Territory: { field: "territory", doctype: "Territory", th: "เขตพื้นที่", en: "Territory" },
  Campaign: { field: "campaign", doctype: "Campaign", th: "แคมเปญ", en: "Campaign" },
};

export function PricingRuleTargetingWidget({ formData, onFieldChange, disabled, isSaving }: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const th = language === "th";
  const ro = disabled || isSaving;
  const [showAdvanced, setShowAdvanced] = useState(false);

  const applicableFor = (formData.applicable_for as string) || "";
  const target = TARGETS[applicableFor];

  const num = (v: unknown) => Number(v) || 0;

  return (
    <div className="px-5 pt-6 pb-5">
      <SectionHeader label={pick(th, "ใช้ได้กับใคร & ตัวเลือก", "Audience & options")} />

      {/* One calm grouped card: audience · stacking · priority — hairline-divided
          rows (label left, control right), regular-weight labels. */}
      <div className="mt-3 divide-y divide-border/40 overflow-clip rounded-xl border border-border bg-card shadow-sm">
        {/* Audience */}
        <div className="px-5 py-4">
          <div className="flex items-center justify-between gap-5">
            <span className="shrink-0 text-sm text-gray-900 dark:text-gray-100">{pick(th, "ใช้ได้กับ", "Available to")}</span>
            <Select value={applicableFor || "__all__"} onValueChange={(v) => onFieldChange("applicable_for", v === "__all__" ? "" : v)} disabled={ro}>
              <SelectTrigger className="h-auto w-auto justify-end gap-1 border-0 bg-transparent p-0 text-sm font-normal text-gray-900 shadow-none outline-none focus:ring-0 dark:text-gray-100 [&>svg]:opacity-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{pick(th, "ลูกค้าทุกคน", "Everyone")}</SelectItem>
                {Object.entries(TARGETS).map(([key, t]) => (
                  <SelectItem key={key} value={key}>
                    {pick(th, `เฉพาะ${t.th}`, `${t.en} only`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {target && (
            <div className="mt-3">
              <DocTypeSearchAutocomplete
                doctype={target.doctype}
                value={(formData[target.field] as string) || ""}
                onChange={(v) => onFieldChange(target.field, v)}
                placeholder={pick(th, `เลือก${target.th}…`, `Select ${target.en.toLowerCase()}…`)}
                minSearchLength={0}
                disabled={ro}
              />
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {pick(th, "จำกัดให้ใช้ได้เฉพาะกลุ่มที่เลือก (ว่าง = ทุกคน)", "Limit who can redeem it (blank = everyone)")}
          </p>
        </div>

        {/* Stacking — toggle */}
        <div className="flex items-center justify-between gap-5 px-5 py-4">
          <span className="min-w-0">
            <span className="block text-sm text-gray-900 dark:text-gray-100">
              {pick(th, "ใช้ร่วมกับโปรโมชันอื่นได้", "Stack with other promotions")}
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {pick(th, "ปิด = ใช้ได้ทีละโปรเท่านั้น", "Off = only one promo applies at a time")}
            </span>
          </span>
          <Switch
            checked={!!formData.apply_multiple_pricing_rules}
            onCheckedChange={(c) => onFieldChange("apply_multiple_pricing_rules", c ? 1 : 0)}
            disabled={ro}
          />
        </div>

        {/* Priority */}
        <div className="flex items-center justify-between gap-5 px-5 py-4">
          <span className="min-w-0">
            <span className="block text-sm text-gray-900 dark:text-gray-100">{pick(th, "ลำดับความสำคัญ", "Priority")}</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {pick(th, "เลขสูง = ได้ใช้ก่อนเมื่อหลายโปรชนกัน", "Higher wins when multiple rules match")}
            </span>
          </span>
          <input
            type="number"
            min={0}
            step={1}
            value={num(formData.priority) || ""}
            placeholder="0"
            onChange={(e) => onFieldChange("priority", Number(e.target.value) || 0)}
            disabled={ro}
            className={cn(NUM_INPUT_CLASS, "w-20")}
          />
        </div>
      </div>

      {/* Advanced (nice-to-have) */}
      <button
        type="button"
        onClick={() => setShowAdvanced((s) => !s)}
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-sky-600 dark:text-gray-400 dark:hover:text-sky-400"
      >
        {pick(th, "ตัวเลือกขั้นสูง", "Advanced options")}
        <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", showAdvanced && "rotate-90")} />
      </button>
      {showAdvanced && (
        <div className="mt-3 divide-y divide-border/40 overflow-clip rounded-xl border border-border/60 bg-muted/20 dark:bg-white/[0.02]">
          {/* Reassure: these are optional extras — remove the "must configure" pressure */}
          <p className="px-5 pt-4 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
            {pick(th, "ตัวเลือกเสริมสำหรับโปรโมชันแบบพิเศษ — คูปองส่วนใหญ่ไม่ต้องตั้งค่าเหล่านี้ ข้ามได้ถ้าไม่แน่ใจ", "Extras for special promotions — most coupons don't need these. Skip if unsure.")}
          </p>
          {/* Discount on another item (buy A → discount B) */}
          <div className="px-5 py-4">
            <div className="text-sm text-gray-900 dark:text-gray-100">
              {pick(th, "ลดราคาสินค้าอีกชิ้น", "Discount a different item")}
            </div>
            <div className="mb-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              {pick(th, "ซื้อชิ้นหนึ่ง แล้วให้ส่วนลดกับอีกชิ้น เช่น ซื้อเสื้อ → กางเกงลดราคา (เว้นว่างไว้ได้)", "Buy one item, discount another — e.g. buy a shirt → the pants get a discount (leave blank to skip)")}
            </div>
            <DocTypeSearchAutocomplete
              doctype="Item"
              value={(formData.other_item_code as string) || ""}
              onChange={(v) => onFieldChange("other_item_code", v)}
              placeholder={pick(th, "เลือกสินค้าที่จะได้ส่วนลด…", "Item that gets the discount…")}
              minSearchLength={0}
              disabled={ro}
            />
          </div>

          {/* Recursion — toggle */}
          <div className="flex items-center justify-between gap-5 px-5 py-4">
            <span className="min-w-0">
              <span className="block text-sm text-gray-900 dark:text-gray-100">{pick(th, "ให้ส่วนลดซ้ำเป็นชุด", "Repeat the discount in sets")}</span>
              <span className="block text-xs leading-relaxed text-gray-500 dark:text-gray-400">{pick(th, "เช่น ทุกๆ 3 ชิ้นที่ซื้อ ได้ส่วนลดเพิ่มอีก 1 ครั้ง (ปิดไว้ถ้าไม่แน่ใจ)", "e.g. every 3 items bought earns the discount again (off if unsure)")}</span>
            </span>
            <Switch checked={!!formData.is_recursive} onCheckedChange={(c) => onFieldChange("is_recursive", c ? 1 : 0)} disabled={ro} />
          </div>
          {!!formData.is_recursive && (
            <div className="flex items-center justify-between gap-5 px-5 py-4">
              <span className="text-sm text-gray-900 dark:text-gray-100">{pick(th, "ทุกๆ (จำนวน)", "Every (qty)")}</span>
              <div className="flex items-center gap-2">
                <input type="number" min={0} step={1} value={num(formData.recurse_for) || ""} placeholder="0"
                  onChange={(e) => onFieldChange("recurse_for", Number(e.target.value) || 0)} disabled={ro} className={cn(NUM_INPUT_CLASS, "w-20")} />
                <span className="text-sm text-gray-500">{pick(th, "ชิ้น", "items")}</span>
              </div>
            </div>
          )}

          {/* Threshold % */}
          <div className="flex items-center justify-between gap-5 px-5 py-4">
            <span className="min-w-0">
              <span className="block text-sm text-gray-900 dark:text-gray-100">{pick(th, "เกณฑ์ขั้นต่ำ (%)", "Minimum threshold (%)")}</span>
              <span className="block text-xs leading-relaxed text-gray-500 dark:text-gray-400">{pick(th, "สำหรับกฎขั้นสูงเท่านั้น — ปล่อยเป็น 0 ได้", "Advanced rules only — leave at 0")}</span>
            </span>
            <input type="number" min={0} step={1} value={num(formData.threshold_percentage) || ""} placeholder="0"
              onChange={(e) => onFieldChange("threshold_percentage", Number(e.target.value) || 0)} disabled={ro} className={cn(NUM_INPUT_CLASS, "w-20")} />
          </div>

          {/* Margin */}
          <div className="flex items-center justify-between gap-5 px-5 py-4">
            <span className="min-w-0">
              <span className="block text-sm text-gray-900 dark:text-gray-100">{pick(th, "มาร์จิน (กำไรขั้นต่ำ)", "Margin (min profit)")}</span>
              <span className="block text-xs leading-relaxed text-gray-500 dark:text-gray-400">{pick(th, "ปกติไม่ใช้กับคูปอง — เลือก “ไม่มี” ได้", "Rarely used for coupons — pick “None”")}</span>
            </span>
            <div className="flex items-center gap-2">
              <Select value={(formData.margin_type as string) || "__none__"} onValueChange={(v) => onFieldChange("margin_type", v === "__none__" ? "" : v)} disabled={ro}>
                <SelectTrigger className="h-auto w-auto justify-end gap-1 border-0 bg-transparent p-0 text-sm font-normal text-gray-900 shadow-none outline-none focus:ring-0 dark:text-gray-100 [&>svg]:opacity-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{pick(th, "ไม่มี", "None")}</SelectItem>
                  <SelectItem value="Percentage">{pick(th, "เปอร์เซ็นต์", "Percentage")}</SelectItem>
                  <SelectItem value="Amount">{pick(th, "จำนวนเงิน", "Amount")}</SelectItem>
                </SelectContent>
              </Select>
              {!!formData.margin_type && (
                <input type="number" min={0} step={1} value={num(formData.margin_rate_or_amount) || ""} placeholder="0"
                  onChange={(e) => onFieldChange("margin_rate_or_amount", Number(e.target.value) || 0)} disabled={ro} className={cn(NUM_INPUT_CLASS, "w-20")} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
